import React, { useState } from 'react';
import * as yup from 'yup';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

interface RingilInputEmailsProps {
  value: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

// Reuse the same email validation as the rest of the app (yup .email()).
const emailSchema = yup.string().email();
const isValidEmail = (email: string): boolean => emailSchema.isValidSync(email);

/**
 * Email-client style multi-value input: type an address and press Enter / comma / semicolon /
 * space (or blur / paste) to turn it into a chip; Backspace on an empty field removes the last
 * chip. Styled to match RingilInputText — the `r-input-wrapper` class lets the surrounding Field
 * apply the standard (and error-state) border.
 */
const RingilInputEmails: React.FC<RingilInputEmailsProps> = ({ value, onChange, disabled = false, placeholder }) => {
  const emails = value ?? [];
  const [draft, setDraft] = useState('');

  const commit = (raw: string) => {
    const parts = raw
      .split(/[,;\s]+/)
      .map(p => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = [...emails];
    parts.forEach(p => {
      if (!next.includes(p)) next.push(p);
    });
    onChange(next);
    setDraft('');
  };

  const removeAt = (index: number) => onChange(emails.filter((_, i) => i !== index));

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ') {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
    } else if (e.key === 'Backspace' && draft === '' && emails.length > 0) {
      e.preventDefault();
      removeAt(emails.length - 1);
    }
  };

  return (
    <div className={twMerge(clsx('r-input-wrapper flex min-h-[2.625rem] w-full flex-wrap items-center gap-1 p-2', disabled && 'opacity-60'))}>
      {emails.map((email, index) => {
        const valid = isValidEmail(email);
        return (
          <span
            key={`${email}-${index}`}
            className={twMerge(
              clsx(
                'inline-flex items-center gap-1 rounded px-2 py-0.5 text-sm',
                valid ? 'bg-[var(--ringil3-brand-light-color)] text-content-secondary' : 'bg-[var(--complementary-errormedium)] text-complementary-error',
              ),
            )}>
            {email}
            {!disabled && (
              <button type={'button'} aria-label={'Remove'} onClick={() => removeAt(index)} className={'cursor-pointer border-0 bg-transparent p-0 text-base leading-none text-current'}>
                ×
              </button>
            )}
          </span>
        );
      })}
      <input
        className={'p-no-border min-w-[60px] flex-1 basis-0 bg-transparent text-base outline-none'}
        value={draft}
        disabled={disabled}
        placeholder={emails.length === 0 ? placeholder : undefined}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => draft.trim() && commit(draft)}
        onPaste={e => {
          const text = e.clipboardData.getData('text');
          if (/[,;\s]/.test(text)) {
            e.preventDefault();
            commit(text);
          }
        }}
      />
    </div>
  );
};

export default RingilInputEmails;